import AddIcon from '@mui/icons-material/Add';
import { Row, Col, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from 'ahooks';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyButton from '../../../components/AlumlyButton';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import { PathBuilder } from '../../../utils/pathBuilder';
import FormFilterNewsList, { FilterNewsT } from './FormFilterNewsList';

const NewsList: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterNewsT>();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, rowCount: 0, loading: false });

  const debouncedFilter = useDebounce(selectedFilter, { wait: 300 });
  const { service: getListService } = useServices(ServiceNameEnum.news.index);

  const getItems = async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await getListService.get({});
    if (data) setItems(data);
    else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
    setLoading(false);
  };

  const filteredItems = useMemo(() => {
    let result = items;
    if (debouncedFilter?.searchQuery) {
      const q = debouncedFilter.searchQuery.toLowerCase();
      result = result.filter((e) =>
        (e.titleML?.en || '').toLowerCase().includes(q) || (e.titleML?.th || '').toLowerCase().includes(q));
    }
    if (debouncedFilter?.status !== undefined && debouncedFilter?.status !== '') {
      const isPublished = debouncedFilter.status === 'true';
      result = result.filter((e) => e.isPublished === isPublished);
    }
    return result;
  }, [items, debouncedFilter]);

  useEffect(() => { setPagination((prev) => ({ ...prev, rowCount: filteredItems.length, page: 0 })); }, [filteredItems]);

  const onRowClick = (rowData: any): void => {
    navigate(PathBuilder.buildPath(RouteNameEnum.news.detail, { params: { id: rowData.id } }));
  };

  const columns: any[] = [
    {
      field: 'image', headerName: 'Image', width: 120,
      renderCell: (params: any) => (
        params.row.coverImageUrl
          ? <img src={params.row.coverImageUrl} alt="" style={{ width: 80, height: 60, borderRadius: 4, objectFit: 'cover' }} />
          : <div style={{ width: 80, height: 60, borderRadius: 4, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography.Text type="secondary" style={{ fontSize: 12 }}>No image</Typography.Text></div>
      ),
    },
    {
      field: 'titleML', headerName: 'Title', width: 250, flex: 1,
      renderCell: (params: any) => (
        <Typography.Text style={{ fontSize: 14, wordWrap: 'break-word', whiteSpace: 'normal' }}>
          {params.row.titleML?.en || params.row.titleML?.th || '-'}
        </Typography.Text>
      ),
    },
    {
      field: 'isPublished', headerName: 'Status', width: 120,
      renderCell: (params: any) => <div><StatusBadge status={params.row.isPublished} /></div>,
    },
    {
      field: 'createdAt', headerName: 'Created', width: 160,
      valueGetter: (params: any) => DateTimeUtils.dateTimeFormat(params.row.createdAt),
    },
  ];

  useEffect(() => { getItems(); }, []);

  return (
    <div style={{ marginTop: 32 }}>
      <Row>
        {checkPermission([PermissionEnum.code.newsCreate]) && (
          <Col xs={24} style={{ textAlign: 'end' }}>
            <AlumlyButton icon={<AddIcon />} label="rxv" width={240} onClick={() => navigate(RouteNameEnum.news.create)}>
              Create new news
            </AlumlyButton>
          </Col>
        )}
      </Row>
      <FormFilterNewsList selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, marginBottom: 24 }}>
        <div style={{ display: 'flex' }}>
          <Typography.Text style={{ marginRight: 8 }}>Total</Typography.Text>
          <Typography.Text strong>{`${filteredItems.length} Items`}</Typography.Text>
        </div>
      </div>
      <AlumlyDataGrid
        rowHeight={80} height={700} rows={filteredItems} columns={columns}
        onRowClick={checkPermission([PermissionEnum.code.newsEdit]) ? onRowClick : undefined}
        {...pagination} loading={loading}
        onPageChange={(page: number) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default NewsList;
